import React, { useState } from 'react';
import {
  PhotoIcon,
  DocumentIcon,
  MusicalNoteIcon,
  FilmIcon,
  CheckIcon,
  EyeIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ArrowRightCircleIcon,
  ArrowLeftCircleIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { EmailAttachment } from '../../tracker/types/tracker.types';

interface AttachmentSelectorProps {
  attachments: EmailAttachment[];
  emailId: string;
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

/**
 * Format file size to human readable string
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Get icon component based on content type
 */
const getFileIcon = (contentType: string, className: string) => {
  const type = contentType?.toLowerCase() || '';

  if (type.startsWith('image/')) {
    return <PhotoIcon className={className} />;
  }
  if (type.startsWith('audio/')) {
    return <MusicalNoteIcon className={className} />;
  }
  if (type.startsWith('video/')) {
    return <FilmIcon className={className} />;
  }
  return <DocumentIcon className={className} />;
};

/**
 * Check if content type is previewable
 */
const isPreviewable = (contentType: string): boolean => {
  const type = contentType?.toLowerCase() || '';
  return (
    type.startsWith('image/') ||
    type.startsWith('audio/') ||
    type.startsWith('video/') ||
    type === 'application/pdf'
  );
};

/**
 * AttachmentSelector - Grid component for selecting which attachments to include
 */
export const AttachmentSelector: React.FC<AttachmentSelectorProps> = ({
  attachments,
  emailId,
  selectedIds,
  onSelectionChange
}) => {
  const [previewAttachment, setPreviewAttachment] = useState<{ attachment: EmailAttachment; index: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Filter out inline attachments and ensure each has a unique ID
  const selectableAttachments = attachments
    .filter(att => !att.isInline && !att.is_inline)
    .map((att, idx) => ({
      ...att,
      // Use original ID if available, otherwise create one from index and name
      uniqueId: att.id || `attachment-${idx}-${att.name}`
    }));
  const selectedCount = selectedIds.length;
  const totalCount = selectableAttachments.length;

  const handleToggle = (attachmentId: string) => {
    if (selectedIds.includes(attachmentId)) {
      onSelectionChange(selectedIds.filter(id => id !== attachmentId));
    } else {
      onSelectionChange([...selectedIds, attachmentId]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(selectableAttachments.map(att => att.uniqueId));
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handlePreview = async (attachment: EmailAttachment, index: number) => {
    if (!isPreviewable(attachment.contentType || attachment.content_type || '')) {
      // For non-previewable files, just download
      const token = localStorage.getItem('authToken');
      const baseURL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
      window.open(`${baseURL}/api/admin/emails/${emailId}/attachments/${index}/download?token=${token}`, '_blank');
      return;
    }

    setPreviewAttachment({ attachment, index });
    setPreviewLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const baseURL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
      const response = await fetch(
        `${baseURL}/api/admin/emails/${emailId}/attachments/${index}/download?inline=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  // Navigate to previous attachment in preview
  const goToPrevAttachment = () => {
    if (!previewAttachment || selectableAttachments.length <= 1) return;

    const currentIdx = selectableAttachments.findIndex(att => att.uniqueId === (previewAttachment.attachment as any).uniqueId);
    const prevIdx = currentIdx <= 0 ? selectableAttachments.length - 1 : currentIdx - 1;
    const prevAttachment = selectableAttachments[prevIdx];

    // Close current preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Load new attachment
    handlePreview(prevAttachment, prevIdx);
  };

  // Navigate to next attachment in preview
  const goToNextAttachment = () => {
    if (!previewAttachment || selectableAttachments.length <= 1) return;

    const currentIdx = selectableAttachments.findIndex(att => att.uniqueId === (previewAttachment.attachment as any).uniqueId);
    const nextIdx = currentIdx >= selectableAttachments.length - 1 ? 0 : currentIdx + 1;
    const nextAttachment = selectableAttachments[nextIdx];

    // Close current preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Load new attachment
    handlePreview(nextAttachment, nextIdx);
  };

  // Toggle current preview attachment selection
  const togglePreviewSelection = () => {
    if (!previewAttachment) return;
    handleToggle((previewAttachment.attachment as any).uniqueId);
  };

  // Check if current preview attachment is selected
  const isPreviewSelected = previewAttachment ? selectedIds.includes((previewAttachment.attachment as any).uniqueId) : false;

  // Get current position in preview
  const getCurrentPreviewPosition = () => {
    if (!previewAttachment) return { current: 0, total: 0 };
    const currentIdx = selectableAttachments.findIndex(att => att.uniqueId === (previewAttachment.attachment as any).uniqueId);
    return { current: currentIdx + 1, total: selectableAttachments.length };
  };

  if (selectableAttachments.length === 0) {
    return null;
  }

  // Partition by selected vs excluded for the two DnD zones
  const includedAttachments = selectableAttachments.filter((a) => selectedIds.includes(a.uniqueId));
  const excludedAttachments = selectableAttachments.filter((a) => !selectedIds.includes(a.uniqueId));

  // Drag activation: require 5px of movement so a normal click still
  // toggles via the existing click handler.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null);
    const { active, over } = event;
    if (!over) return;
    const id = String(active.id);
    const target = String(over.id); // 'included' | 'excluded'
    const isCurrentlySelected = selectedIds.includes(id);
    if (target === 'included' && !isCurrentlySelected) {
      onSelectionChange([...selectedIds, id]);
    } else if (target === 'excluded' && isCurrentlySelected) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    }
  };

  const draggingAttachment = draggingId
    ? selectableAttachments.find((a) => a.uniqueId === draggingId)
    : null;

  return (
    <div className="attachment-selector">
      {/* Header */}
      <div className="attachment-selector__header">
        <h4 className="attachment-selector__title">
          Pièces jointes — {selectedCount} sur {totalCount} {selectedCount !== 1 ? 'seront ajoutées' : 'sera ajoutée'} au dossier
        </h4>
        <div className="attachment-selector__actions">
          <button
            type="button"
            className="attachment-selector__action-btn"
            onClick={handleSelectAll}
          >
            Tout inclure
          </button>
          <button
            type="button"
            className="attachment-selector__action-btn"
            onClick={handleDeselectAll}
          >
            Tout exclure
          </button>
        </div>
      </div>

      {/* DnD: two columns — Sera ajouté (left) and Exclu (right).
          Drag to move; click to toggle (5 px drag threshold preserves the
          click). The selected state is the source of truth — zones are
          rendered by partitioning. */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="attachment-selector__buckets">
          <DropZone
            id="included"
            title="Sera ajouté au dossier"
            count={includedAttachments.length}
            tone="good"
            emptyHint="Glissez des pièces jointes ici pour les inclure"
            icon={<CheckCircleIcon className="attachment-selector__bucket-icon" />}
          >
            {includedAttachments.map((attachment) => (
              <DraggableCard
                key={attachment.uniqueId}
                attachment={attachment}
                indexInAll={selectableAttachments.findIndex((a) => a.uniqueId === attachment.uniqueId)}
                isSelected
                isDragging={draggingId === attachment.uniqueId}
                onToggle={() => handleToggle(attachment.uniqueId)}
                onPreview={handlePreview}
              />
            ))}
          </DropZone>

          <DropZone
            id="excluded"
            title="Exclu"
            count={excludedAttachments.length}
            tone="muted"
            emptyHint="Tout est inclus"
            icon={<NoSymbolIcon className="attachment-selector__bucket-icon" />}
          >
            {excludedAttachments.map((attachment) => (
              <DraggableCard
                key={attachment.uniqueId}
                attachment={attachment}
                indexInAll={selectableAttachments.findIndex((a) => a.uniqueId === attachment.uniqueId)}
                isSelected={false}
                isDragging={draggingId === attachment.uniqueId}
                onToggle={() => handleToggle(attachment.uniqueId)}
                onPreview={handlePreview}
              />
            ))}
          </DropZone>
        </div>

        <DragOverlay>
          {draggingAttachment ? (
            <CardChrome
              attachment={draggingAttachment}
              isSelected={selectedIds.includes(draggingAttachment.uniqueId)}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Help text */}
      <p className="attachment-selector__help">
        Cliquez pour basculer, ou glissez-déposez entre les deux colonnes. L'icône œil prévisualise sans modifier la sélection.
      </p>

      {/* Preview Modal */}
      {previewAttachment && (
        <div className="attachment-selector__preview-modal" onClick={closePreview}>
          {/* Navigation Arrow - Previous */}
          {selectableAttachments.length > 1 && (
            <button
              type="button"
              className="attachment-selector__nav-btn attachment-selector__nav-btn--prev"
              onClick={(e) => { e.stopPropagation(); goToPrevAttachment(); }}
            >
              <ChevronLeftIcon />
            </button>
          )}

          <div className="attachment-selector__preview-container" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="attachment-selector__preview-close"
              onClick={closePreview}
            >
              <XMarkIcon />
            </button>

            <div className="attachment-selector__preview-header">
              <div className="attachment-selector__preview-title">
                <h4>{previewAttachment.attachment.name}</h4>
                <span className="attachment-selector__preview-position">
                  {getCurrentPreviewPosition().current} / {getCurrentPreviewPosition().total}
                </span>
              </div>
              <span>{formatFileSize(previewAttachment.attachment.size)}</span>
            </div>

            <div className="attachment-selector__preview-content">
              {previewLoading ? (
                <div className="attachment-selector__preview-loading">
                  Chargement...
                </div>
              ) : previewUrl ? (
                <>
                  {(previewAttachment.attachment.contentType || previewAttachment.attachment.content_type || '').startsWith('image/') && (
                    <img src={previewUrl} alt={previewAttachment.attachment.name} />
                  )}
                  {(previewAttachment.attachment.contentType || previewAttachment.attachment.content_type || '') === 'application/pdf' && (
                    <object data={previewUrl} type="application/pdf" width="100%" height="100%">
                      <p>Impossible d'afficher le PDF. <a href={previewUrl} target="_blank" rel="noopener noreferrer">Télécharger</a></p>
                    </object>
                  )}
                  {(previewAttachment.attachment.contentType || previewAttachment.attachment.content_type || '').startsWith('audio/') && (
                    <audio controls src={previewUrl} />
                  )}
                  {(previewAttachment.attachment.contentType || previewAttachment.attachment.content_type || '').startsWith('video/') && (
                    <video controls src={previewUrl} />
                  )}
                </>
              ) : (
                <div className="attachment-selector__preview-error">
                  Impossible de charger la prévisualisation
                </div>
              )}
            </div>

            {/* Footer with selection button */}
            <div className="attachment-selector__preview-footer">
              <button
                type="button"
                className={`attachment-selector__toggle-btn ${isPreviewSelected ? 'attachment-selector__toggle-btn--selected' : 'attachment-selector__toggle-btn--deselected'}`}
                onClick={togglePreviewSelection}
              >
                {isPreviewSelected ? (
                  <>
                    <MinusIcon className="attachment-selector__toggle-icon" />
                    <span>Retirer des documents</span>
                  </>
                ) : (
                  <>
                    <PlusIcon className="attachment-selector__toggle-icon" />
                    <span>Ajouter aux documents</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Navigation Arrow - Next */}
          {selectableAttachments.length > 1 && (
            <button
              type="button"
              className="attachment-selector__nav-btn attachment-selector__nav-btn--next"
              onClick={(e) => { e.stopPropagation(); goToNextAttachment(); }}
            >
              <ChevronRightIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentSelector;

/* ─── DnD sub-components (private to this module) ────────────────── */

interface DropZoneProps {
  id: 'included' | 'excluded';
  title: string;
  count: number;
  tone: 'good' | 'muted';
  emptyHint: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ id, title, count, tone, emptyHint, icon, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={[
        'attachment-selector__bucket',
        `attachment-selector__bucket--${tone}`,
        isOver ? 'attachment-selector__bucket--over' : '',
      ].join(' ').trim()}
    >
      <div className="attachment-selector__bucket-head">
        {icon}
        <span className="attachment-selector__bucket-title">{title}</span>
        <span className="attachment-selector__bucket-count">{count}</span>
      </div>
      <div className="attachment-selector__bucket-body">
        {count === 0 ? (
          <div className="attachment-selector__bucket-empty">{emptyHint}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

interface DraggableCardProps {
  attachment: EmailAttachment & { uniqueId: string };
  indexInAll: number;
  isSelected: boolean;
  isDragging: boolean;
  onToggle: () => void;
  onPreview: (attachment: EmailAttachment, index: number) => void;
}

const DraggableCard: React.FC<DraggableCardProps> = ({ attachment, indexInAll, isSelected, isDragging, onToggle, onPreview }) => {
  const { setNodeRef, listeners, attributes, isDragging: dragActive } = useDraggable({
    id: attachment.uniqueId,
  });
  const contentType = attachment.contentType || attachment.content_type || '';
  const canPreview = isPreviewable(contentType);
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={[
        'attachment-selector__card',
        isSelected ? 'attachment-selector__card--selected' : 'attachment-selector__card--deselected',
        (isDragging || dragActive) ? 'attachment-selector__card--dragging' : '',
      ].join(' ').trim()}
      onClick={(e) => {
        // The drag listener attached above triggers on movement; pure
        // clicks (no movement past 5 px) fall through here.
        const target = e.target as HTMLElement;
        if (target.closest('button')) return;
        onToggle();
      }}
    >
      <button
        type="button"
        className="attachment-selector__quick-toggle"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        title={isSelected ? 'Exclure' : 'Inclure'}
      >
        {isSelected ? <ArrowRightCircleIcon className="w-4 h-4" /> : <ArrowLeftCircleIcon className="w-4 h-4" />}
      </button>
      <button
        type="button"
        className="attachment-selector__preview-btn"
        onClick={(e) => { e.stopPropagation(); onPreview(attachment, indexInAll); }}
        title={canPreview ? 'Prévisualiser' : 'Télécharger'}
      >
        <EyeIcon className="attachment-selector__preview-icon" />
      </button>
      <div className="attachment-selector__icon">
        {getFileIcon(contentType, 'attachment-selector__file-icon')}
      </div>
      <div className="attachment-selector__info">
        <span className="attachment-selector__name" title={attachment.name}>{attachment.name}</span>
        <span className="attachment-selector__size">{formatFileSize(attachment.size)}</span>
      </div>
    </div>
  );
};

interface CardChromeProps {
  attachment: EmailAttachment & { uniqueId: string };
  isSelected: boolean;
  isOverlay?: boolean;
}

const CardChrome: React.FC<CardChromeProps> = ({ attachment, isSelected, isOverlay }) => {
  const contentType = attachment.contentType || attachment.content_type || '';
  return (
    <div
      className={[
        'attachment-selector__card',
        isSelected ? 'attachment-selector__card--selected' : 'attachment-selector__card--deselected',
        isOverlay ? 'attachment-selector__card--overlay' : '',
      ].join(' ').trim()}
    >
      <div className="attachment-selector__icon">{getFileIcon(contentType, 'attachment-selector__file-icon')}</div>
      <div className="attachment-selector__info">
        <span className="attachment-selector__name">{attachment.name}</span>
        <span className="attachment-selector__size">{formatFileSize(attachment.size)}</span>
      </div>
    </div>
  );
};
